import React, { useEffect, useState, useMemo } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  Box,
  Divider,
  Paper,
  TableContainer,
  TablePagination,
  CircularProgress,
} from '@mui/material'

import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  HighlightOff as HighlightOffIcon,
  SaveOutlined as SaveOutlinedIcon,
  AddOutlined as AddIcon,
} from '@mui/icons-material/'

import { TOAST_SETTINGS_ERROR } from '../../../utility/constants'
import { useAuthContext } from '../../../hooks/useAuthContext'
import ConfirmationBox from '../../../components/Dashboard/ConfirmationBox'
import { useNavigate } from 'react-router-dom'
import { getAxiosOptions } from '../../../services/serviceHelper'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

// Simple debounce hook for search inputs
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

const ListConfig = () => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [lists, setLists] = useState([])

  // Search and Pagination states
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 200)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  // Edit list states
  const [isEditListDialogOpen, setIsEditListDialogOpen] = useState(false)
  const [activeList, setActiveList] = useState(null)
  const [editName, setEditName] = useState('')
  const [editMdkValue, setEditMdkValue] = useState('')
  const [editMetadataKeys, setEditMetadataKeys] = useState([])

  // Create list states
  const [isCreateListDialogOpen, setIsCreateListDialogOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createMdkValue, setCreateMdkValue] = useState('')
  const [createMetadataKeys, setCreateMetadataKeys] = useState([])

  const [deletingListId, setDeletingListId] = useState(-1)
  const [isDeleteListConfirmationBoxOpen, setIsDeleteListConfirmationBoxOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  // Fetch lists definition (enriched with items_count from backend subquery)
  useEffect(() => {
    if (!user) return

    const getListsFromServer = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get(`${NODE_BASE_URL}/template/list/company`, getAxiosOptions(user))
        setLists(response.data || [])
      } catch {
        toast.error('Failed to load lists.', TOAST_SETTINGS_ERROR)
      } finally {
        setIsLoading(false)
      }
    }

    getListsFromServer()
  }, [user])

  const refreshListsFromServer = async () => {
    if (!user) return
    setIsLoading(true)
    try {
      const response = await axios.get(`${NODE_BASE_URL}/template/list/company`, getAxiosOptions(user))
      setLists(response.data || [])
    } catch {
      toast.error('Failed to load lists.', TOAST_SETTINGS_ERROR)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenCreateDialog = () => {
    setCreateName('')
    setCreateMdkValue('')
    setCreateMetadataKeys([])
    setIsCreateListDialogOpen(true)
  }

  const handleSaveNewList = async () => {
    if (!user) return
    if (!createName.trim()) {
      toast.error('List name cannot be empty.', TOAST_SETTINGS_ERROR)
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        list_name: createName.trim(),
        metadata_keys: createMetadataKeys.length > 0 ? createMetadataKeys.join(',') : null,
      }
      const response = await axios.post(
        `${NODE_BASE_URL}/admin/template/list/add-list`,
        payload,
        getAxiosOptions(user)
      )
      toast.success('List created successfully.')
      setIsCreateListDialogOpen(false)
      const newListId = response.data?.id
      if (newListId) {
        navigate(`/admin/lists/${newListId}`)
      } else {
        await refreshListsFromServer()
      }
    } catch {
      toast.error('Failed to create new list.', TOAST_SETTINGS_ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddCreateMetadataKey = () => {
    let key = createMdkValue.toLowerCase().replace(/[^a-z0-9_]/gi, '')
    if (!key) return
    if (createMetadataKeys.includes(key)) {
      toast.warn('Metadata key already exists.')
      return
    }
    setCreateMetadataKeys([...createMetadataKeys, key])
    setCreateMdkValue('')
  }

  const handleDeleteCreateMetadataKey = (index) => {
    const updated = [...createMetadataKeys]
    updated.splice(index, 1)
    setCreateMetadataKeys(updated)
  }

  async function deleteList(listId) {
    if (!user) return
    setIsDeleting(true)
    try {
      await axios.delete(
        `${NODE_BASE_URL}/admin/template/list/delete-list/${listId}`,
        getAxiosOptions(user)
      )
      toast.success('List deleted successfully.')
      setLists(lists.filter((l) => l.id !== listId))
    } catch {
      toast.error(
        'Unable to delete list. It may be currently used in a task.',
        TOAST_SETTINGS_ERROR
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const handleOpenEditDialog = (list) => {
    setActiveList(list)
    setEditName(list.list_name || '')
    const mdk = list.metadata_keys ? list.metadata_keys.split(',').map((elem) => elem.trim()) : []
    setEditMetadataKeys(mdk)
    setEditMdkValue('')
    setIsEditListDialogOpen(true)
  }

  const handleSaveListEdit = async () => {
    if (!user || !activeList) return
    if (!editName.trim()) {
      toast.error('List name cannot be empty.', TOAST_SETTINGS_ERROR)
      return
    }

    setIsSaving(true)
    try {
      // 1. Save list name if changed
      if (editName.trim() !== activeList.list_name) {
        await axios.put(
          `${NODE_BASE_URL}/admin/template/list/edit-list-name`,
          {
            list_id: activeList.id,
            list_name: editName.trim(),
          },
          getAxiosOptions(user)
        )
      }

      // 2. Save metadata keys if changed
      const oldKeys = activeList.metadata_keys || ''
      const newKeysStr = editMetadataKeys.join(',')
      if (oldKeys !== newKeysStr) {
        await axios.put(
          `${NODE_BASE_URL}/admin/template/list/edit-list-metadata`,
          {
            list_id: activeList.id,
            metadata: newKeysStr || null,
          },
          getAxiosOptions(user)
        )
      }

      toast.success('List details updated successfully.')
      setIsEditListDialogOpen(false)
      setActiveList(null)
      await refreshListsFromServer()
    } catch {
      toast.error('Failed to save list details.', TOAST_SETTINGS_ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddMetadataKey = () => {
    let key = editMdkValue.toLowerCase().replace(/[^a-z0-9_]/gi, '')
    if (!key) return
    if (editMetadataKeys.includes(key)) {
      toast.warn('Metadata key already exists.')
      return
    }
    setEditMetadataKeys([...editMetadataKeys, key])
    setEditMdkValue('')
  }

  const handleDeleteMetadataKey = (index) => {
    const updated = [...editMetadataKeys]
    updated.splice(index, 1)
    setEditMetadataKeys(updated)
  }

  // Memoized search filtering
  const filteredLists = useMemo(() => {
    if (!debouncedSearchQuery) return lists
    const q = debouncedSearchQuery.toLowerCase()
    return lists.filter((list) => {
      const nameMatch = list.list_name?.toLowerCase().includes(q)
      const mdkMatch = list.metadata_keys?.toLowerCase().includes(q)
      const placeholderMatch = list.placeholders?.some((p) => p.toLowerCase().includes(q))
      const linkedProcessesMatch = list.linked_processes?.toLowerCase().includes(q)
      return nameMatch || mdkMatch || placeholderMatch || linkedProcessesMatch
    })
  }, [lists, debouncedSearchQuery])

  // Paginated lists
  const paginatedLists = useMemo(() => {
    const start = page * rowsPerPage
    return filteredLists.slice(start, start + rowsPerPage)
  }, [filteredLists, page, rowsPerPage])

  return (
    <Container maxWidth="lg" sx={{ py: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h6" color="textPrimary" fontWeight="700">
            List Configuration
          </Typography>
          {/* <Typography variant="subtitle1" color="textSecondary">
            Manage lists definitions, metadata, and templates placeholders
          </Typography> */}
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search lists..."
            size="small"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(0)
            }}
            sx={{ width: 320, backgroundColor: 'white' }}
          />
          <Button
            color="primary"
            variant="outlined"
            onClick={() => navigate('/admin/lddm')}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, height: '40px' }}
          >
            Manage P-C Relationships
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, height: '40px' }}
          >
            Create New List
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600, py: 1.25, width: '5%' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25, width: '22%' }}>List Name</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, py: 1.25, width: '10%' }}>Items Count</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25, width: '18%' }}>Metadata Keys</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25, width: '18%' }}>Placeholders</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1.25, width: '17%' }}>Linked Processes</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, py: 1.25, pr: 3, width: '10%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={30} />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Loading list configurations...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedLists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="textSecondary">
                    {"No lists found. Create a new list or refine your search."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedLists.map((list, index) => {
                const keys = list.metadata_keys ? list.metadata_keys.split(',') : []
                const placeholders = list.placeholders || []
                const rowNum = page * rowsPerPage + index + 1
                return (
                  <TableRow
                    key={list.id}
                    hover
                    onClick={() => navigate(`/admin/lists/${list.id}`)}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell sx={{ py: 0.75 }}>{rowNum}</TableCell>
                    <TableCell sx={{ py: 0.75, fontWeight: 600, color: 'primary.main' }}>
                      {list.list_name}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75, fontWeight: 500 }}>
                      {list.items_count || 0}
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {keys.length === 0 ? (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '11px' }}>
                            None
                          </Typography>
                        ) : (
                          keys.map((k, i) => (
                            <Chip
                              key={i}
                              label={k}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '10px',
                                height: '18px',
                                fontWeight: 500,
                                backgroundColor: '#f3f4f6',
                                borderColor: '#e5e7eb',
                              }}
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {placeholders.length === 0 ? (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '11px' }}>
                            None
                          </Typography>
                        ) : (
                          placeholders.map((p, i) => (
                            <Chip
                              key={i}
                              label={p}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{
                                fontSize: '10px',
                                height: '18px',
                                fontWeight: 500,
                                backgroundColor: '#eff6ff',
                                borderColor: '#bfdbfe',
                              }}
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {list.linked_processes ? (
                          list.linked_processes.split(',').map((p) => p.trim()).map((proc, i) => (
                            <Chip
                              key={i}
                              label={proc}
                              size="small"
                              variant="outlined"
                              title={proc}
                              sx={{
                                fontSize: '10px',
                                height: '18px',
                                fontWeight: 500,
                                color: '#16a34a',
                                backgroundColor: '#f0fdf4',
                                borderColor: '#bbf7d0',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '11px' }}>
                            None
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75, pr: 3 }}>
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenEditDialog(list)
                          }}
                          size="small"
                          title="Edit Settings & Keys"
                        >
                          <EditOutlinedIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                        {lists.length > 1 && (
                          <IconButton
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeletingListId(list.id)
                              setIsDeleteListConfirmationBoxOpen(true)
                            }}
                            size="small"
                            title="Delete List"
                          >
                            <DeleteOutlinedIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredLists.length}
          page={page}
          onPageChange={(event, newPage) => { event?.toString(); setPage(newPage) }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 20))
            setPage(0)
          }}
          rowsPerPageOptions={[20, 50, 100, 200]}
        />
      </TableContainer>

      {/* Delete List Confirmation */}
      {isDeleteListConfirmationBoxOpen && (
        <ConfirmationBox
          title={`Are you sure you want to delete the list '${
            lists.find((l) => l.id === deletingListId)?.list_name
          }'?`}
          isDialogOpen={isDeleteListConfirmationBoxOpen}
          setIsDialogOpen={setIsDeleteListConfirmationBoxOpen}
          onYes={() => {
            deleteList(deletingListId)
            setDeletingListId(-1)
            setIsDeleteListConfirmationBoxOpen(false)
          }}
          disabled={isDeleting}
        />
      )}

      {/* Create List Dialog */}
      <Dialog
        open={isCreateListDialogOpen}
        onClose={() => !isSaving && setIsCreateListDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="600">
            Create New List
          </Typography>
          <IconButton onClick={() => setIsCreateListDialogOpen(false)} disabled={isSaving} size="small">
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary" fontWeight="600" mb={1}>
              List Details
            </Typography>
            <TextField
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              size="small"
              label="List Name"
              variant="outlined"
              fullWidth
              disabled={isSaving}
              autoFocus
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="textSecondary" fontWeight="600" mb={1}>
              Metadata Keys
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {createMetadataKeys.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  No metadata keys added.
                </Typography>
              ) : (
                createMetadataKeys.map((mdk, i) => (
                  <Chip
                    key={i}
                    label={mdk}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => handleDeleteCreateMetadataKey(i)}
                    disabled={isSaving}
                    sx={{ fontWeight: 500 }}
                  />
                ))
              )}
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                value={createMdkValue}
                onChange={(e) => setCreateMdkValue(e.target.value)}
                placeholder="new_metadata_key"
                size="small"
                variant="outlined"
                disabled={isSaving}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddCreateMetadataKey()
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddCreateMetadataKey}
                disabled={isSaving || !createMdkValue.trim()}
                sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '8px', p: 1 }}
              >
                <SaveOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setIsCreateListDialogOpen(false)}
            color="secondary"
            disabled={isSaving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewList}
            color="primary"
            variant="contained"
            disabled={isSaving || !createName.trim()}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isSaving ? 'Creating...' : 'Create List'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit List (Name & Metadata Keys) Dialog */}
      <Dialog
        open={isEditListDialogOpen}
        onClose={() => !isSaving && setIsEditListDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="600">
            Edit List Settings
          </Typography>
          <IconButton onClick={() => setIsEditListDialogOpen(false)} disabled={isSaving} size="small">
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary" fontWeight="600" mb={1}>
              List Details
            </Typography>
            <TextField
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              label="List Name"
              variant="outlined"
              fullWidth
              disabled={isSaving}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="textSecondary" fontWeight="600" mb={1}>
              Metadata Keys
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {editMetadataKeys.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  No metadata keys added.
                </Typography>
              ) : (
                editMetadataKeys.map((mdk, i) => (
                  <Chip
                    key={i}
                    label={mdk}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => handleDeleteMetadataKey(i)}
                    disabled={isSaving}
                    sx={{ fontWeight: 500 }}
                  />
                ))
              )}
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                value={editMdkValue}
                onChange={(e) => setEditMdkValue(e.target.value)}
                placeholder="new_metadata_key"
                size="small"
                variant="outlined"
                disabled={isSaving}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddMetadataKey()
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddMetadataKey}
                disabled={isSaving || !editMdkValue.trim()}
                sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '8px', p: 1 }}
              >
                <SaveOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setIsEditListDialogOpen(false)}
            color="secondary"
            disabled={isSaving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveListEdit}
            color="primary"
            variant="contained"
            disabled={isSaving}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default ListConfig
